"use client";

import React from "react";
import Image from "next/image";

// Right-side "AI Workflows" dashboard image (person at desk). 582 x 728.
const LAB_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/ai_labs_right.webp";

const points = [
  {
    num: "01.",
    title: "Capability you can see",
    desc: "Your team builds real agents and workflows, not just completes modules",
  },
  {
    num: "02.",
    title: "Zero risk, zero prerequisites",
    desc: "A safe build environment, no client data, no firm licenses required",
  },
  {
    num: "03.",
    title: "Adoption that sticks",
    desc: "Hands-on building which turns training into a team that uses AI on Monday",
  },
];

type Agent = { name: string; desc: string };
type Card = { label: string; thumb: string; agents: Agent[] };

const cards: Card[] = [
  {
    label: "CFO Teams",
    thumb:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/cfo_team.webp",
    agents: [
      {
        name: "AP 3-Way Match Agent",
        desc: "Matches invoice, PO, and receiving report / GRN; flags exception invoices",
      },
      {
        name: "AR Collection & Reminder Agent",
        desc: "Tracks receivables and sends reminders that escalate over time",
      },
      {
        name: "Bank Reconciliation Agent",
        desc: "Runs a quick BRS, then flags and explains the differences",
      },
      {
        name: "Employee Expense Agent",
        desc: "Checks reimbursements against policy; flags exceptions with reasoning",
      },
    ],
  },
  {
    label: "Audit",
    thumb:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/audit_team.webp",
    agents: [
      {
        name: "PBC Tracker Agent",
        desc: "Tracks documents sent by clients and organizes the files",
      },
      {
        name: "Revenue Recognition Agent",
        desc: "Computes monthly revenue & deferred revenue schedule",
      },
      {
        name: "AR Confirmation Agent",
        desc: "Sends confirmations to the sample, tracks responses, follows up on gaps",
      },
      {
        name: "Lease Audit Agent",
        desc: "Checks the lease expense schedule against the contract, flags exception",
      },
    ],
  },
  {
    label: "Tax",
    thumb:
      "https://ik.imagekit.io/mileseducation/miles_website/caira/new/tax_team.webp",
    agents: [
      {
        name: "PBC Agent",
        desc: "Requests and tracks W-2s, 1099s, K-1s, and the prior-year return; flags what's missing",
      },
      {
        name: "1099 Agent",
        desc: "Collects contractor W-9s, tracks year-end 1099s for review",
      },
      {
        name: "Tax Estimation Agent",
        desc: "Estimates this year's tax from prior-year returns to support early payments",
      },
      {
        name: "R&D Credits Agent",
        desc: "Suggests where and how the client can claim R&D credits",
      },
    ],
  },
];

const AgentCard = ({ card }: { card: Card }) => (
  <div className="flex flex-col rounded-[10px] border border-[rgba(255,255,255,0.06)] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_45.018px_0_rgba(166,137,255,0.30)] backdrop-blur-[4.92px] p-[10px] max-md:shrink-0 max-md:w-[280px] max-md:snap-start">
    {/* Header */}
    <p className="text-[#FFBA08] font-bold text-sm leading-tight">
      {card.label}
    </p>
    <p className="text-white font-bold text-sm leading-tight">
      AI Agents &amp; Workflows
    </p>

    {/* Thumbnail strip */}
    <Image
      src={card.thumb}
      alt={`${card.label} instructors`}
      width={375}
      height={134}
      className="w-full h-auto rounded-md mt-3"
    />

    {/* Agents 2x2 grid */}
    <div className="grid grid-cols-2 gap-x-[8px] gap-y-[8px] mt-4">
      {card.agents.map((a) => (
        <div
          key={a.name}
          className="rounded-[4px] bg-[rgba(33,33,60,0.50)] p-[5px] md:h-[83px] h-[70px]"
        >
          <p className="text-white font-bold text-[10px] leading-tight">
            {a.name}
          </p>
          <p className="text-white text-[10px] leading-[1.4] mt-1">{a.desc}</p>
        </div>
      ))}
    </div>

    <p className="text-white font-semibold text-[8px] text-right mt-auto pt-1">
      And many more
    </p>
  </div>
);

const MilesAiLabs = () => {
  return (
    <section className="w-full bg-black py-7 md:py-[50px]">
      <div className="main_container mx-auto px-4 md:px-6 md:max-w-[1280px]">
        {/* Title */}
        <div className="text-center mb-6 md:mb-10">
          <h2 className="text-[22px] md:text-[30px] font-bold text-[#FFBA08] leading-tight">
            Miles AI Labs
          </h2>
          <p className="text-white text-sm md:text-base font-semibold mt-1">
            Hands-on practice with agents &amp; workflows
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* LEFT — points + cards */}
          <div className="flex-1 min-w-0">
            {/* Points (+ dashboard image beside them on mobile) */}
            <div className="flex gap-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 flex-1 min-w-0">
                {points.map((p) => (
                  <div key={p.num}>
                    <p className="text-white font-bold text-xs md:text-base leading-tight">
                      <span className="mr-1 text-base md:text-[24px]">
                        {p.num}
                      </span>
                      {p.title}
                    </p>
                    <p className="text-[#FFFFFF99] text-[10px] md:text-sm leading-[1.5] mt-1 md:mt-1.5 font-medium">
                      {p.desc}
                    </p>
                  </div>
                ))}
              </div>

              {/* Mobile-only dashboard image */}
              <div className="md:hidden w-[44%] shrink-0 self-start">
                <Image
                  src={LAB_IMG}
                  alt="Miles AI Labs — AI workflows dashboard"
                  width={582}
                  height={728}
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>

            <Image
              src="https://ik.imagekit.io/mileseducation/miles_website/caira/new/hr_line.webp"
              alt=""
              width={510}
              height={1}
              className="w-full h-auto my-5 md:my-6"
            />

            {/* Team cards */}
            <div className="grid grid-cols-3 gap-3 md:gap-3 max-md:flex max-md:overflow-x-auto max-md:snap-x max-md:pb-1 max-md:[scrollbar-width:none]">
              {cards.map((card) => (
                <AgentCard key={card.label} card={card} />
              ))}
            </div>
          </div>

          {/* RIGHT — dashboard image (desktop) */}
          <div className="hidden md:block md:w-[380px] shrink-0 self-stretch">
            <Image
              src={LAB_IMG}
              alt="Miles AI Labs — AI workflows dashboard"
              width={582}
              height={728}
              className="w-full h-full rounded-xl object-cover object-center"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MilesAiLabs;
